import { KsService, useApiClient } from '@siclop/kstone-vue'
import type { Organization, OrganizationForm } from '@siclop/ksadmin-vue'
import { ensureSuccess } from '@/shared/utils/apiResult'

/** Compteurs d'activité de « Mon entreprise » (GET company/activity-counts). */
export interface CompanyActivityCounts {
  /** Tous les projets visibles par l'utilisateur (pas seulement ceux dont l'entreprise est cliente) */
  projects: number
  /** Tous les documents de l'explorateur « Documents de l'entreprise », sous-dossiers compris */
  documents: number
}

/**
 * Service for managing the main company organization
 */
export class CompanyService extends KsService {
  protected static mainUrl = '/organizations'

  /**
   * Compteurs « Projets » et « Documents » de l'écran Mon entreprise : une route de comptage,
   * sans charger les listes.
   */
  static async getActivityCounts(): Promise<CompanyActivityCounts> {
    const client = useApiClient()
    const response = await client.get<Partial<CompanyActivityCounts> | null>('/company/activity-counts')
    return {
      projects: Number(response?.projects ?? 0),
      documents: Number(response?.documents ?? 0)
    }
  }

  /**
   * Get the main company organization (configured via CompanySettings:OrganizationId)
   */
  static async getMyCompany(): Promise<Organization> {
    const client = useApiClient()
    const response = await client.get<{ success: boolean; result: Organization; errors: string[] }>(`${this.mainUrl}/my-company`)
    if (!response.success || !response.result) {
      throw new Error(response.errors?.join(', ') || 'Failed to load company')
    }
    return response.result
  }

  /**
   * Update the main company organization
   */
  static async updateMyCompany(form: OrganizationForm): Promise<Organization> {
    const company = await this.getMyCompany()
    const client = useApiClient()
    form.id = company.id
    // `put` ne lève pas : un refus de l'API revient en `{ success: false, errors }`
    const result = await client.put(`${this.mainUrl}/${company.id}`, form)
    return ensureSuccess<Organization>(result, 'Erreur lors de l\'enregistrement de l\'entreprise')
  }
}
